'use client'

import { Button } from '@/components/ui/button'
import { LoginDialog } from '@/components/auth/login-dialog'
import { useGuestSearchLimit } from '@/hooks/useGuestSearchLimit'
import { IconLock } from '@tabler/icons-react'

interface SearchLimitBannerProps {
  /** Additional class names */
  className?: string
  /** Variant - 'inline' shows minimal text, 'card' shows full card */
  variant?: 'inline' | 'card'
  /** Whether offline mode (local SQLite) is enabled */
  isOfflineMode?: boolean
}

/**
 * Banner/notice shown to guest users about their search limit
 *
 * - Shows full card when limit is reached
 * - Shows inline warning when approaching limit
 * - Hidden for authenticated users, offline mode users, or when plenty of searches remain
 */
export function SearchLimitBanner({
  className = '',
  variant = 'card',
  isOfflineMode = false
}: SearchLimitBannerProps) {
  const {
    isGuest,
    remaining,
    isLimited,
    limit,
    showWarning,
    isOfflineUnlimited
  } = useGuestSearchLimit(isOfflineMode)

  // Don't show anything for authenticated users or offline mode users
  if (!isGuest || isOfflineUnlimited) return null

  // Limit reached - show prominent card
  if (isLimited) {
    if (variant === 'inline') {
      return (
        <div
          className={`text-muted-foreground flex items-center gap-2 text-sm ${className}`}
        >
          <IconLock className="h-4 w-4" />
          <span>Daily limit reached.</span>
          <LoginDialog>
            <button className="text-primary underline underline-offset-2">
              Sign in
            </button>
          </LoginDialog>
          <span>for unlimited searches.</span>
        </div>
      )
    }

    return (
      <div
        className={`rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-900/50 dark:bg-amber-950/30 ${className}`}
      >
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/50">
            <IconLock className="h-5 w-5 text-amber-600 dark:text-amber-400" />
          </div>
          <div className="flex-1">
            <p className="font-medium text-amber-800 dark:text-amber-200">
              Daily search limit reached
            </p>
            <p className="text-muted-foreground mt-1 text-sm">
              You&apos;ve used all {limit} free searches for today. Sign in for
              unlimited access.
            </p>
            <LoginDialog>
              <Button size="sm" className="mt-3">
                Sign in
              </Button>
            </LoginDialog>
          </div>
        </div>
      </div>
    )
  }

  // Show warning when approaching limit
  if (showWarning) {
    return (
      <div className={`text-muted-foreground text-xs ${className}`}>
        {remaining} search{remaining !== 1 ? 'es' : ''} remaining today.{' '}
        <LoginDialog>
          <button className="text-primary underline underline-offset-2">
            Sign in
          </button>
        </LoginDialog>{' '}
        for unlimited.
      </div>
    )
  }

  // Plenty of searches remaining - don't show anything
  return null
}

/**
 * Compact version that only shows when limit is reached
 * Used in search results area
 */
export function SearchLimitNotice({
  className = '',
  isOfflineMode = false
}: {
  className?: string
  isOfflineMode?: boolean
}) {
  const { isGuest, isLimited, isOfflineUnlimited } =
    useGuestSearchLimit(isOfflineMode)

  if (!isGuest || !isLimited || isOfflineUnlimited) return null

  return (
    <div
      className={`bg-muted/50 text-muted-foreground rounded-lg p-4 text-center text-sm ${className}`}
    >
      <p>Daily search limit reached.</p>
      <LoginDialog>
        <Button variant="link" className="h-auto p-0">
          Sign in for unlimited searches
        </Button>
      </LoginDialog>
    </div>
  )
}
